/**
 * Keep embedded CSRF tokens in step with the session.
 *
 * A cached HTML fragment carries whatever authenticity_token was current when
 * it was written, and the plan canvas caches slots and trays that contain
 * `button_to` and `form_with`. The cache is shared, so the second person to
 * open a plan is served the first person's token and their submit fails with
 * InvalidAuthenticityToken -- a 422 on a button that worked perfectly for
 * whoever happened to warm the fragment.
 *
 * This is the mechanism rails-ujs used to ship as refreshCSRFTokens(), for
 * exactly this reason: the token that is always correct is the one in
 * <meta name="csrf-token">, which Rails re-renders per request and never
 * caches. Rails accepts that global token in place of a per-form one, so
 * overwriting the embedded token with it is safe everywhere, not only in
 * cached markup.
 */
const tokenMeta = () => document.querySelector("meta[name=csrf-token]")?.content

const tokenParam = () =>
  document.querySelector("meta[name=csrf-param]")?.content || "authenticity_token"

// Note the selector: `input[name=...]`, NOT `form input[name=...]`. The submit
// handler below passes the form ITSELF as the root, and a descendant selector
// starting at `form` looks for a form nested inside that form -- which never
// exists, so the token would silently not be refreshed at the one moment it
// matters most. Any input by that name is a CSRF token whether or not a `form`
// ancestor sits between it and the root.
const refreshWithin = (root) => {
  const token = tokenMeta()
  if (!token || !root?.querySelectorAll) return

  root.querySelectorAll(`input[name="${tokenParam()}"]`).forEach((input) => {
    input.value = token
  })
}

const refreshCsrfTokens = () => refreshWithin(document)

// After every navigation and every morph, because a morph is exactly when
// cached fragments arrive: turbo:load covers first paint and Drive visits,
// turbo:morph the refresh-and-morph that canvas mutations end in, and
// turbo:frame-render a lazy frame arriving with a cached body inside it.
;["turbo:load", "turbo:morph", "turbo:frame-render"].forEach((event) => {
  document.addEventListener(event, refreshCsrfTokens)
})

// And once more at the moment of truth. The events above cannot cover every
// way markup reaches the page -- a turbo-stream replace, a fragment injected
// by a Stimulus controller -- and a stale token is only ever a problem when
// something is submitted. Capture phase, so this runs before Turbo serialises
// the form.
document.addEventListener(
  "submit",
  (event) => {
    if (event.target instanceof HTMLFormElement) refreshWithin(event.target)
  },
  true
)

export default refreshCsrfTokens
